import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconColor, IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import IconTypes from '@aurora/shared-client/icons';
import React, { useState, useEffect } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import localStyles from './SideNavigationWithMobileOverlay.module.pcss';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';

interface Props {
  /**
   * Class name(s) to apply to the wrapping element.
   */
  className?: string;

  /**
   * Title of the guide navigation
   */
  buttonTitle: string;
}

/**
 * Guide navigation
 *
 * @author Amit Agrawal, Willi Hyde
 */
const SideNavigationWithMobileOverlay: React.FC<React.PropsWithChildren<Props>> = ({
  buttonTitle,
  className,
  children
}) => {
  const cx = useClassNameMapper(localStyles);
  const { router } = useEndUserRoutes();
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [isButtonSticky, setIsButtonSticky] = useState<boolean>();

  // Update the overlay state to false when the route changes upon clicking an article link
  useEffect(() => {
    const routeChangeStart = url => {
      if (router.asPath !== url) {
        setShowOverlay(false);
      }
    };
    router.events.on('routeChangeStart', routeChangeStart);
    return () => {
      router.events.off('routeChangeStart', routeChangeStart);
    };
  }, [router]);

  // Adds a class to the body tag called 'lia-overlay-active' when the guide navigation is open
  useEffect(() => {
    if (showOverlay) {
      document.body.classList.add(cx('lia-overlay-active'));
    } else {
      document.body.classList.remove(cx('lia-overlay-active'));
    }
  }, [showOverlay, cx]);

  // Update the state based on scroll position to determine if button is sticky when overlay is closed
  useEffect(() => {
    const handleScroll = () => {
      setIsButtonSticky(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div
      className={cx(className, 'lia-side-navigation', {
        'lia-overlay-active': showOverlay
      })}
    >
      <Button
        className={cx('lia-button', {
          'lia-is-active': showOverlay,
          'lia-is-sticky-active': isButtonSticky
        })}
        variant={ButtonVariant.NO_VARIANT}
        onClick={() => setShowOverlay(!showOverlay)}
        aria-expanded={showOverlay}
      >
        <span className={cx('lia-g-clamp lia-g-clamp-1')}>{buttonTitle}</span>
        <Icon
          className={cx('lia-button-icon', { 'lia-is-active': showOverlay })}
          size={IconSize.PX_12}
          color={IconColor.GRAY_900}
          icon={IconTypes.ChevronDownIcon}
        />
      </Button>
      <nav
        className={cx('lia-container', {
          'lia-is-hidden': !showOverlay
        })}
        onScroll={(event: React.UIEvent): void =>
          setIsButtonSticky(event.currentTarget.scrollTop > 0)
        }
      >
        {children}
      </nav>
    </div>
  );
};

export default SideNavigationWithMobileOverlay;
